/**
 * API Configuration & Base Utilities
 *
 * Central configuration for all API calls. Currently uses local data,
 * but designed for seamless migration to a real REST API or CMS.
 *
 * To switch to a live API:
 *   1. Set BASE_URL to your API endpoint
 *   2. Toggle USE_MOCK_DATA to false
 *   3. All contentService methods will automatically fetch from the API
 */

const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || '',
  USE_MOCK_DATA: true,
  TIMEOUT: 10000,
  HEADERS: {
    'Content-Type': 'application/json',
  },
};

/**
 * Generic fetch wrapper with timeout, error handling, and retries.
 * @param {string} endpoint - API endpoint path
 * @param {object} options  - Fetch options (method, body, headers, etc.)
 * @returns {Promise<any>}
 */
async function apiFetch(endpoint, options = {}) {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      headers: { ...API_CONFIG.HEADERS, ...options.headers },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(`Request to ${endpoint} timed out after ${API_CONFIG.TIMEOUT}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export { API_CONFIG, apiFetch };
export default API_CONFIG;
